import React from 'react';
import { Constants } from 'acscp/components/common/popover/util';
import { createPortal, findDOMNode } from 'react-dom';
import { CSSProperties } from 'react';
import { RefObject } from 'react';
import { ReactPortal } from 'react';
import debounce from 'lodash.debounce';

// Based on the MIT licensed react-tiny-popover available at https://www.npmjs.com/package/react-tiny-popover

export interface ContentLocation {
    top: number;
    left: number;
}

export interface ContentRendererArgs {
    position: Position;
    align: Align | undefined;
    nudge: {top: number, left: number};
    targetRect: ClientRect;
}

export type ContentRenderer = (args: ContentRendererArgs) => JSX.Element;

export declare type Position = 'left' | 'right' | 'top' | 'bottom';
export declare type Align = 'start' | 'center' | 'end';

export declare interface PopoverProps {
    children: JSX.Element;
    isOpen: boolean;
    content: ContentRenderer | JSX.Element;
    padding?: number;
    position: Position;
    onClickOutside?: (e: MouseEvent) => void;
    containerClassName?: string;
    containerStyle?: Partial<CSSStyleDeclaration>;
    align?: Align;
    transitionDuration?: number;
    windowBorderPadding?: number;
}

interface ButtonLaunchingPopoverState {
    showPortal: boolean;
    opacity: number;
    top: number;
    left: number;
}

export default class CustomReactPopover extends React.Component<PopoverProps, ButtonLaunchingPopoverState> {
    private targetElementRef: Element | null = null;
    private readonly popoverDivRef: RefObject<HTMLDivElement>;

    static defaultProps: Partial<PopoverProps> = {
        padding: Constants.DEFAULT_PADDING,
        windowBorderPadding: Constants.DEFAULT_WINDOW_PADDING,
        align: 'center',
        containerClassName: Constants.POPOVER_CONTAINER_CLASS_NAME,
    };

    constructor(props: Readonly<PopoverProps>) {
        super(props);
        this.state = { showPortal: props.isOpen, opacity: 0, top: 0, left: 0 };
        this.popoverDivRef = React.createRef<HTMLDivElement>();
    }

    componentDidMount(): void {
        window.addEventListener('click', this.onClick);
        window.addEventListener('resize', this.windowResizeHandler);
    }

    componentWillUnmount(): void {
        window.removeEventListener('click', this.onClick);
        window.removeEventListener('resize', this.windowResizeHandler);
    }

    componentDidUpdate(prevProps: Readonly<PopoverProps>, prevState: Readonly<ButtonLaunchingPopoverState>): void {
        if (this.targetElementRef == null) {
            const childrenRef = findDOMNode(this);
            if (childrenRef != null && childrenRef instanceof Element) {
                this.targetElementRef = childrenRef;
            }
        }

        if (prevProps.isOpen !== this.props.isOpen) {
            this.setState({showPortal: this.props.isOpen});
        }

        if (!prevState.showPortal) {
            this.updatePositionIfShowing();
        }
    }

    private onClick = (e: MouseEvent) => {
        const { onClickOutside, isOpen } = this.props;
        const { showPortal } = this.state;

        const target = e.target as Node;

        if (showPortal && onClickOutside && isOpen &&
                !(this.targetElementRef && this.targetElementRef.contains(target)) &&
                !(this.popoverDivRef.current && this.popoverDivRef.current.contains(target))) {
            onClickOutside(e);
        }
    }

    private onResize = (): void => {
        this.updatePositionIfShowing();
    }

    private updatePositionIfShowing = (): void => {
        if (this.state.showPortal) {
            const { top, left } = this.getTargetLocation(
                this.props.position,
                CustomReactPopover.getElementBoundingRectOrEmpty(this.targetElementRef),
                CustomReactPopover.getRefBoundingRectOrEmpty(this.popoverDivRef));
            const finalLeft = left + window.pageXOffset;
            const finalTop = top + window.pageYOffset;

            this.setState({top: finalTop, left: finalLeft, opacity: 1});
        }
    }

    private getTargetLocation(position: Position, newTargetRect: ClientRect, popoverRect: ClientRect): ContentLocation {
        const { padding, align } = this.props;

        const paddingOrZero = padding || 0;
        const targetMidX = newTargetRect.left + (newTargetRect.width / 2);
        const targetMidY = newTargetRect.top + (newTargetRect.height / 2);
        let top: number = 0;
        let left: number = 0;

        switch (position) {
            case 'top':
                top = newTargetRect.top - popoverRect.height - paddingOrZero;
                left = targetMidX - (popoverRect.width / 2);
                if (align === 'start') { left = newTargetRect.left; }
                if (align === 'end') { left = newTargetRect.right - popoverRect.width; }
                break;
            case 'left':
                top = targetMidY - (popoverRect.height / 2);
                left = newTargetRect.left - paddingOrZero - popoverRect.width;
                if (align === 'start') { top = newTargetRect.top; }
                if (align === 'end') { top = newTargetRect.bottom - popoverRect.height; }
                break;
            case 'bottom':
                top = newTargetRect.bottom + paddingOrZero;
                left = targetMidX - (popoverRect.width / 2);
                if (align === 'start') { left = newTargetRect.left; }
                if (align === 'end') { left = newTargetRect.right - popoverRect.width; }
                break;
            case 'right':
                top = targetMidY - (popoverRect.height / 2);
                left = newTargetRect.right + paddingOrZero;
                if (align === 'start') { top = newTargetRect.top; }
                if (align === 'end') { top = newTargetRect.bottom - popoverRect.height; }
                break;
        }

        return { top, left };
    }

    render(): JSX.Element {
        return (
            <>
                {this.props.children}
                {this.state.showPortal && this.portalElement()}
            </>
        );
    }

    private portalElement(): ReactPortal {
        return createPortal(this.newCreatePopoverDiv(), window.document.body);
    }

    private newCreatePopoverDiv(): JSX.Element {
        const { containerClassName, transitionDuration } = this.props;
        const { top, left, opacity } = this.state;

        const styleProps: CSSProperties = {
            position: 'absolute',
            top: `${top.toFixed()}px`,
            left: `${left.toFixed()}px`,
            overflow: 'visible',
            opacity,
            transition: `opacity ${transitionDuration || Constants.FADE_TRANSITION}s`
        };

        return (
            <div ref={this.popoverDivRef} style={styleProps} className={containerClassName}>
                {this.getContent()}
            </div>
        );
    }

    private getContent(): JSX.Element {
        const { align, content, position } = this.props;

        if (typeof content !== 'function') {
            return content;
        }

        const popoverRect = CustomReactPopover.getRefBoundingRectOrEmpty(this.popoverDivRef);
        const targetPosition = this.getTargetLocation(
            this.props.position,
            CustomReactPopover.getElementBoundingRectOrEmpty(this.targetElementRef),
            popoverRect);
        const targetRect = {
            top: targetPosition.top,
            left: targetPosition.left,
            width: popoverRect.width,
            height: popoverRect.height,
            bottom: popoverRect.bottom - (targetPosition.top - popoverRect.top),
            right: popoverRect.right - (targetPosition.left - popoverRect.left)
        } as ClientRect;
        const {left: adjustedLeft, top: adjustedTop} = this.getAdjustedPopoverPosition(targetRect);

        return (
            content({
                nudge: {
                    left: adjustedLeft - targetPosition.left,
                    top: adjustedTop - targetPosition.top
                },
                targetRect,
                position,
                align
            })
        );
    }

    private getAdjustedPopoverPosition({ top, left, width, height }: Partial<ClientRect>): ContentLocation {
        const { windowBorderPadding } = this.props;
        const padding = windowBorderPadding || 0;

        const minTopToFitTopEdge = padding;
        const maxTopToFitBottomEdge = document.body.clientHeight - padding - (height || 0);
        const adjustedTop = CustomReactPopover.constrainOffset(top || 0, minTopToFitTopEdge, maxTopToFitBottomEdge);

        const minLeftToFitLeftEdge = padding;
        const maxLeftToFitRightEdge = document.body.clientWidth - padding - (width || 0);
        const adjustedLeft = CustomReactPopover.constrainOffset(left || 0, minLeftToFitLeftEdge, maxLeftToFitRightEdge);

        return { top: adjustedTop, left: adjustedLeft };
    }

    // Returns:
    //  - value if min <= value <= max
    //  - min if value < min < max
    //  - max if min < max < value
    //  - avg(min, max) if max < min
    private static constrainOffset(value: number, min: number, max: number): number {
        if (max < min) {
            return (min + max) / 2;
        }

        const valueAdjustedForMin = value < min ? min : value;
        return valueAdjustedForMin > max ? max : valueAdjustedForMin;
    }

    private static getRefBoundingRectOrEmpty(ref: RefObject<HTMLDivElement>): ClientRect {
        return ref.current ? ref.current.getBoundingClientRect() : Constants.EMPTY_CLIENT_RECT;
    }

    private static getElementBoundingRectOrEmpty(element: Element | null): ClientRect {
        return element ? element.getBoundingClientRect() : Constants.EMPTY_CLIENT_RECT;
    }

    private windowResizeHandler: () => void = debounce(this.onResize, 10);
}
